"use client";

import type { NormalizedEmail } from "./types";

// Browser-side client for the Maila AI Gateway — the same PHP backend the
// mobile app talks to (C:\SwiftMind\email_cleaner\backend). Every AI feature
// (chat, summarize, search, reply, rewrite, cleanup, priority, rules, scam,
// subscriptions, spending, calendar, tasks) goes through one of these
// endpoints, exactly like AiGatewayClient in the Flutter app.

const BASE_URL = process.env.NEXT_PUBLIC_MAILA_AI_GATEWAY_URL ?? "";

export class AiGatewayError extends Error {}

async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new AiGatewayError(typeof data.error === "string" ? data.error : `Request to ${path} failed`);
  }
  return data as T;
}

export type CleanupSuggestion = {
  category: string;
  reason: string;
  estimated_count: number;
  estimated_bytes: number;
  query: string;
};

export const aiGateway = {
  chat: (message: string, history: { role: string; content: string }[], emailContext: NormalizedEmail[]) =>
    post<{ reply: string }>("/api/chat", { message, history, email_context: emailContext }),

  summarize: (scope: "email" | "thread" | "daily" | "weekly", content: string) =>
    post<{ summary: string }>("/api/summarize", { scope, content }),

  search: (naturalLanguageQuery: string) => post<{ query: string }>("/api/search", { query: naturalLanguageQuery }),

  reply: (emailContent: string, tone: string) =>
    post<{ reply: string }>("/api/reply", { email_content: emailContent, tone }),

  rewrite: (draft: string, style: string) => post<{ rewritten: string }>("/api/rewrite", { draft, style }),

  cleanup: (emails: NormalizedEmail[]) => post<{ suggestions: CleanupSuggestion[] }>("/api/cleanup", { emails }),

  priority: (emails: NormalizedEmail[]) =>
    post<{ priorities: { id: string; score: number; reason: string }[] }>("/api/priority", { emails }),

  generateRule: (instruction: string) =>
    post<{
      condition_type: string;
      condition_value: string;
      action_type: string;
      action_value: string;
      description: string;
    }>("/api/rules", { instruction }),

  scamCheck: (emailContent: string) => post<{ is_scam: boolean; reason: string }>("/api/scam", { email_content: emailContent }),

  analyzeSubscriptions: (senders: { sender: string; name: string; count: number; dates: string[] }[]) =>
    post<{ subscriptions: { sender: string; frequency: string }[] }>("/api/subscriptions", { emails: senders }),

  spendingInsights: (receipts: { from: string; subject: string; snippet: string; date: string }[]) =>
    post<{ categories: { name: string; amount: number }[]; total: number; insights: string[] }>("/api/spending", {
      receipts,
    }),

  extractCalendarEvents: (emailContent: string) =>
    post<{ events: { title: string; date_time: string; location: string }[] }>("/api/calendar", {
      email_content: emailContent,
    }),

  extractTasks: (emailContent: string) => post<{ tasks: string[] }>("/api/tasks", { email_content: emailContent }),
};
